import { useState } from 'react';
import {
    Box,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Stack,
    useTheme,
} from '@mui/material';

import Button from '@components/Button';
import { useAdminRole } from '@hooks/useAdminRole';
import { useAdminPanelStore } from '@store/adminPanelStore';

import AgentConfiguration from './components/AgentConfiguration';
import AvatarConfiguration from './components/AvatarConfiguration';
import { GeneralPredefinedPrompts } from './components/GeneralPredefinedPrompts/GeneralPredefinedPrompts';
import Menu from './components/Menu';
import { PredefinedPromptsByRole } from './components/PredefinedPromptsByRoleProps/PredefinedPromptsByRole';
import { RegionalKnowledgeFiles } from './components/RegionalKnowledgeFiles/RegionalKnowledgeFiles';
import SnackbarWithAlert from './components/SnackbarWithAlert';
import { useUpdateForm } from './hooks/useUpdateForm';
import { buttons, headerTitle, menuItems } from './constants';
import { MenuOptionValue } from './types';

const AdminPanel = () => {
    const isAdminPanelOpen = useAdminPanelStore((state) => state.isOpen);
    const toggleAdminPanelVisibility = useAdminPanelStore((state) => state.toggle);
    const theme = useTheme();
    const { isGlobalAdmin, isLocalAdmin } = useAdminRole();

    const defaultMenuOptionForGlobalAdmin = menuItems[0].value;
    const defaultMenuOptionForLocalAdmin = menuItems.find(
        (item) => item.value === MenuOptionValue.RegionalKnowledgeFiles
    )!.value;
    const defaultMenuOption = isGlobalAdmin
        ? defaultMenuOptionForGlobalAdmin
        : defaultMenuOptionForLocalAdmin;
    const [selectedMenuOption, setSelectedMenuOption] =
        useState<MenuOptionValue>(defaultMenuOption);

    const formState = useUpdateForm(selectedMenuOption);

    const renderSelectedContent = () => {
        switch (formState.type) {
            case MenuOptionValue.AgentConfiguration:
                return (
                    <AgentConfiguration mutate={formState.mutate} isSending={formState.isSending} />
                );
            case MenuOptionValue.RegionalKnowledgeFiles:
                return (
                    <RegionalKnowledgeFiles
                        mutate={formState.mutate}
                        isSending={formState.isSending}
                    />
                );
            case MenuOptionValue.AvatarConfiguration:
                return (
                    <AvatarConfiguration
                        mutate={formState.mutate}
                        isSending={formState.isSending}
                    />
                );
            case MenuOptionValue.GeneralPredefinedPrompts:
                return (
                    <GeneralPredefinedPrompts
                        mutate={formState.mutate}
                        isSending={formState.isSending}
                    />
                );
            case MenuOptionValue.PredefinedPromptsByRole:
                return (
                    <PredefinedPromptsByRole
                        mutate={formState.mutate}
                        isSending={formState.isSending}
                    />
                );
        }
    };

    return (
        <>
            <Dialog
                open={isAdminPanelOpen}
                onClose={toggleAdminPanelVisibility}
                fullWidth
                maxWidth='xl'
                slotProps={{
                    paper: {
                        sx: {
                            height: '80%',
                        },
                    },
                }}
            >
                <DialogTitle>{headerTitle}</DialogTitle>
                <DialogContent dividers>
                    <Stack direction='row' sx={{ position: 'relative', height: '100%' }}>
                        <Menu
                            selectedMenuOption={selectedMenuOption}
                            setSelectedMenuOption={setSelectedMenuOption}
                            isGlobalAdmin={isGlobalAdmin}
                            isLocalAdmin={isLocalAdmin}
                        />
                        <Divider orientation='vertical' flexItem />
                        <Box
                            sx={{
                                flex: 1,
                                overflow: 'auto',
                                scrollbarGutter: 'stable',
                                px: 8,
                                pt: 2,
                            }}
                        >
                            {renderSelectedContent()}
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    {formState.isSending && (
                        <CircularProgress
                            size={24}
                            sx={{
                                color: theme.palette.customColors.purple,
                            }}
                        />
                    )}
                    <Button
                        type='submit'
                        form={selectedMenuOption}
                        disabled={formState.isSending}
                        lighterFont
                    >
                        {buttons.submit}
                    </Button>
                    <Button onClick={toggleAdminPanelVisibility} lighterFont>
                        {buttons.close}
                    </Button>
                </DialogActions>
            </Dialog>

            <SnackbarWithAlert
                isError={formState.isError}
                isSuccess={formState.isSuccess}
                error={formState.error}
                selectedMenuOption={selectedMenuOption}
            />
        </>
    );
};

export default AdminPanel;
